import { useState } from 'react';
import { getBotProfile, listBotAudiences, listBotLibraries, putBotProfile } from '../../api/endpoints';
import type { BotAudience, BotLibrary } from '../../api/types';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Field } from '../../components/Field';
import { Spinner } from '../../components/Spinner';
import { Toggle } from '../../components/Toggle';
import { useLoad } from '../../lib/useLoad';

/**
 * The assistant on a page that is NOT for customers — a chauffeur portal today.
 *
 * The page says who it is for (`audience: 'driver'` in the embed snippet) and
 * the service answers as that: different words for the person, and none of the
 * customer-account tools, because a chauffeur has no bookings of their own to
 * look up. What an operator adds here is the part only they can write — what
 * their chauffeurs need told — beside, never instead of, the business context
 * every audience gets.
 *
 * There is deliberately no on/off switch. The snippet is the switch: a page
 * that does not name an audience is a customer page, which is what every
 * install was before this existed.
 */
export function BotAudiences() {
  const audiences = useLoad(listBotAudiences);
  const rows = (audiences.data ?? []).filter((audience) => !audience.isDefault);

  if (audiences.loading) return null;
  if (audiences.error) return null;
  if (!rows.length) return null;

  return (
    <section className="card card-pad manage-card">
      <div className="manage-card-head">
        <div>
          <h2 className="manage-card-title">Other people it answers</h2>
          <p className="manage-card-sub">
            The same assistant, on a page that is not for customers. Add{' '}
            <code>audience</code> to the snippet on that page — the whole line is under{' '}
            <strong>Install</strong>, as the Chauffeurs version — and it answers as your assistant for those
            people instead: their words, their questions, and none of the reservation tools that
            belong to a customer.
          </p>
        </div>
      </div>
      {rows.map((audience) => (
        <AudienceRow key={audience.id} audience={audience} onSaved={() => audiences.reload()} />
      ))}
    </section>
  );
}

function AudienceRow({ audience, onSaved }: { audience: BotAudience; onSaved: () => void }) {
  const libraries = useLoad(() => listBotLibraries(audience.id));
  const [context, setContext] = useState(audience.context ?? '');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Read-modify-write of the WHOLE audiences map: flags merge per top-level
   * key server-side, so sending only this audience's entry would drop the
   * others' — the same reason the library toggles above re-read first.
   */
  const persist = async (patch: { context?: string; knowledgeLibraries?: string[] }) => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const profile = await getBotProfile();
      const current = profile?.flags?.audiences ?? {};
      const mine = current[audience.id] ?? { context: null, knowledgeLibraries: [] };
      await putBotProfile({
        flags: {
          ...(profile?.flags ?? {}),
          audiences: {
            ...current,
            [audience.id]: {
              context: patch.context ?? mine.context ?? '',
              knowledgeLibraries: patch.knowledgeLibraries ?? mine.knowledgeLibraries ?? [],
            },
          },
        },
      });
      setSaved(true);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (library: BotLibrary, next: boolean) => {
    const current = libraries.data?.selected ?? [];
    await persist({
      knowledgeLibraries: next
        ? [...new Set([...current, library.id])]
        : current.filter((id) => id !== library.id),
    });
    libraries.reload();
  };

  const entries = libraries.data?.payload ?? [];
  const available = libraries.data?.available ?? true;

  return (
    <div className="bot-audience">
      <div className="bot-audience-head">
        <span className="bot-library-name">{audience.label}</span>
        <span className="bot-library-sub">{audience.describe}</span>
      </div>

      {error && <ErrorNote error={error} />}
      {saved && <Note tone="ok">Saved.</Note>}

      <Field
        label={`What it should know for ${audience.label.toLowerCase()}`}
        hint="Added to your business context on these pages only. Leave empty and they get the same standing instructions as everyone else."
      >
        <textarea
          className="textarea"
          rows={4}
          value={context}
          onChange={(e) => setContext(e.target.value)}
          placeholder="Pay runs every second Friday. Chauffeurs report vehicle damage through the app before the next job…"
        />
      </Field>
      <div className="manage-card-actions manage-card-actions-start">
        <button
          className="btn btn-primary"
          disabled={busy || context === (audience.context ?? '')}
          onClick={() => void persist({ context })}
        >
          {busy ? <Spinner /> : 'Save'}
        </button>
      </div>

      {!available ? (
        <Note tone="warn">
          Ready-made answers could not be loaded just now. Any you switched on are not being used
          until this clears.
        </Note>
      ) : entries.length ? (
        <ul className="bot-library-list">
          {entries.map((library) => (
            <li key={library.id}>
              <div className="bot-library-text">
                <span className="bot-library-name">{library.name}</span>
                <span className="bot-library-sub">{library.tagline}</span>
                {/* Said plainly, because switching this on is a choice about
                    whose words get read out: some libraries were written for
                    your office rather than for the person in the chat. */}
                {library.writtenFor === 'operator' && (
                  <span className="bot-library-sub">
                    Written for your office, not for {audience.label.toLowerCase()} — the
                    assistant is told to use only the part that concerns them, and never to
                    repeat something only your team could do.
                  </span>
                )}
              </div>
              <Toggle
                checked={library.enabled}
                disabled={busy}
                onChange={(next) => void toggle(library, next)}
                title={library.enabled ? 'On' : 'Off'}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="manage-card-sub">
          There are no ready-made answers for {audience.label.toLowerCase()} yet — the assistant
          answers them from your business context and your own entries.
        </p>
      )}
    </div>
  );
}
